import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import minMax from "dayjs/plugin/minMax";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { PredefinedDateRangeType } from "@/enumerations/Dates/DateRangePredefined/PredefinedDateRangeType";

// These extensions are applied globally at app startup via the side-effect
// import in main.ts (equivalent of the old plugins/dayjs.ts), so other modules
// (e.g. PaymentAmountHelpers) can rely on them regardless of import order.
dayjs.extend(relativeTime);
dayjs.extend(minMax);
dayjs.extend(isSameOrAfter);

export const dateTimeFormat = "MM/DD/YYYY hh:mm A";
export const dateTimeShortFormat = "M/D H:mm";
export const periodFormat = "MMMM YYYY";
export const standardFormat = "MM/DD/YYYY";
export const extendedFormat = "MMMM D, YYYY";
export const extendedFormatWithWeekday = "dddd, MMMM D, YYYY";

//Earlier dates are refused by the read rather than answered with an empty grid, and typing a year walks
//through 0002, 0020 and 0202 on the way to the one meant. Legacy draws the same line.
export const earliestSelectableDate = "1901-01-01";

//The other end of the same rule. The reads bound a range by adding a day to the end date, which runs off the
//calendar on the last date the type holds, so the API refuses it - and without a matching ceiling here the
//grower gets that refusal as a failure banner instead of a message against the date they typed.
export const latestSelectableDate = "9999-12-30";

export function isBeforeEarliestSelectableDate(
  date: string | null | undefined
): boolean {
  return !!date && date < earliestSelectableDate;
}

export function isAfterLatestSelectableDate(
  date: string | null | undefined
): boolean {
  return !!date && date > latestSelectableDate;
}

//A date input accepts a five-digit year, and every check below compares date strings - "20261-01-01" sorts
//after the floor and after the other end, so it passes all of them and the API cannot read it. Only the year
//is judged, because that is the only part of a date the input can hand over in a length the API will refuse.
function hasUnreadableYear(date: string | null | undefined): boolean {
  return !!date && !/^\d{4}-/.test(date);
}

/**
 * The message a From/To pair earns, or an empty string when it is one the read can carry. Shared so the two
 * pages offering a custom range cannot drift apart over what counts as an error. Either end may stand alone;
 * only a pair is judged for order.
 */
export function getDateRangeError(
  beginDate: string | null | undefined,
  endDate: string | null | undefined
): string {
  //First, because a date the API cannot read would otherwise be reported as whichever comparison it lost.
  if (hasUnreadableYear(beginDate) || hasUnreadableYear(endDate)) {
    return "Dates must have a four digit year";
  }

  if (
    isBeforeEarliestSelectableDate(beginDate) ||
    isBeforeEarliestSelectableDate(endDate)
  ) {
    return `Dates must be on or after ${getFormattedDateString(
      earliestSelectableDate
    )}`;
  }

  if (
    isAfterLatestSelectableDate(beginDate) ||
    isAfterLatestSelectableDate(endDate)
  ) {
    return `Dates must be on or before ${getFormattedDateString(
      latestSelectableDate
    )}`;
  }

  return beginDate && endDate && endDate < beginDate
    ? "To date cannot be earlier than From date"
    : "";
}

export function getPredefinedDateRangeStartDate(
  currentDate: dayjs.Dayjs,
  presetDateRangeType: string
): dayjs.Dayjs {
  let presetFromDate = currentDate;

  if (presetDateRangeType === PredefinedDateRangeType.ThreeMonths) {
    presetFromDate = currentDate.add(-3, "month").set("date", 1);
  } else if (presetDateRangeType === PredefinedDateRangeType.SixMonths) {
    presetFromDate = currentDate.add(-6, "month").set("date", 1);
  } else if (presetDateRangeType === PredefinedDateRangeType.OneYear) {
    presetFromDate = currentDate.add(-1, "year").set("date", 1);
  } else if (presetDateRangeType === PredefinedDateRangeType.CurrentYear) {
    presetFromDate = currentDate.set("date", 1).set("month", 0);
  } else if (presetDateRangeType === PredefinedDateRangeType.PreviousYear) {
    presetFromDate = currentDate
      .set("date", 1)
      .set("month", 0)
      .set("year", dayjs().add(-1, "year").get("year"));
  }

  return presetFromDate;
}

export function getPredefinedDateRangeEndDate(
  currentDate: dayjs.Dayjs,
  presetDateRangeType: string
): dayjs.Dayjs {
  let presetToDate = currentDate;

  if (presetDateRangeType === PredefinedDateRangeType.PreviousYear) {
    presetToDate = currentDate
      .set("month", 11)
      .set("date", 31)
      .set("year", dayjs().add(-1, "year").get("year"));
  }

  return presetToDate;
}

export function getFormattedDateString(
  date: string | Date | dayjs.Dayjs | null | undefined,
  format = standardFormat
): string {
  if (!date) return "";
  return dayjs(date).format(format).toString();
}

export function getRelativeTimeAgo(date: string | Date | dayjs.Dayjs): string {
  if (!date) return "";
  return dayjs(date).fromNow();
}

export function getIsoDateString(date: string | Date | dayjs.Dayjs): string {
  if (!date) return "";
  return dayjs(date).toISOString().substring(0, 10);
}

export function getOneYearFromToday(currentDate: dayjs.Dayjs): string {
  return currentDate.add(1, "year").toISOString();
}

export function getFirstDayOfTheMonth(currentDate: dayjs.Dayjs): string {
  return currentDate.startOf("month").toISOString();
}

export function getLastDayOfTheMonth(currentDate: dayjs.Dayjs | Date): string {
  return dayjs(currentDate)
    .set("date", dayjs(currentDate).daysInMonth())
    .toISOString();
}

export function getCurrentDate(): dayjs.Dayjs {
  return dayjs();
}

export function getCustomOrPredefinedStartDate(
  isCustomDateRange: boolean,
  customStartDate: string,
  predefinedDateRangeTypeValue: string
): string {
  const fromDate = isCustomDateRange
    ? customStartDate
    : predefinedDateRangeTypeValue === PredefinedDateRangeType.AllDates
    ? ""
    : getPredefinedDateRangeStartDate(
        getCurrentDate(),
        predefinedDateRangeTypeValue
      );

  return getFormattedDateString(fromDate);
}

export function getCustomOrPredefinedEndDate(
  isCustomDateRange: boolean,
  customEndDate: string,
  predefinedDateRangeTypeValue: string
): string {
  const toDate = isCustomDateRange
    ? customEndDate
    : predefinedDateRangeTypeValue === PredefinedDateRangeType.AllDates
    ? ""
    : getPredefinedDateRangeEndDate(
        getCurrentDate(),
        predefinedDateRangeTypeValue
      );

  return getFormattedDateString(toDate);
}

export function getDayOfMonth(date: string | Date | dayjs.Dayjs): number {
  return dayjs(date).date();
}

export function getDayOfWeek(date: string | Date | dayjs.Dayjs): string {
  return dayjs(date).format("dddd");
}

export function getDateOrdinal(day: number): string {
  return (
    day +
    (31 === day || 21 === day || 1 === day
      ? "st"
      : 22 === day || 2 === day
      ? "nd"
      : 23 === day || 3 === day
      ? "rd"
      : "th")
  );
}

export function getMonthName(month: number): string {
  return getCurrentDate().month(month).format("MMMM");
}
